import {
  ModalidadDia,
  ModalidadRenglon,
} from "src/app/shared/model/asistencia/asistencia.model";

/**
 * Patrón semanal de una persona MIXTO, tal como lo edita el diálogo: para
 * cada día, dónde trabaja por la mañana y dónde por la tarde, igual que en
 * el kiosco. Así se captura el día partido —en la oficina por la mañana y
 * desde casa por la tarde—, que antes el web no podía crear.
 *
 * Vive aparte del componente para poder probarlo sin montar el diálogo.
 * Arranca con el patrón vigente que ya manda el servidor en `renglon.dias`
 * (ModalidadBuss.completarPatronMixto): arrancar con la semana en oficina
 * hacía que "Cambiar modalidad" + Guardar pisara el patrón de la persona.
 */

/** `plantilla.do` cuelga el patrón vigente en `dias` a los renglones MIXTO. */
export type RenglonConPatron = ModalidadRenglon & { dias?: ModalidadDia[] | null };

/** Cada mitad del día sólo puede ser en sitio o desde casa. */
export type LugarDia = "OFICINA" | "HOME_OFFICE";

/** Un renglón del editor: un día con su mañana y su tarde. */
export interface DiaPatron {
  /** 1 = lunes ... 7 = domingo, como los numera el backend. */
  diaSemana: number;
  nombre: string;
  manana: LugarDia;
  tarde: LugarDia;
}

export const DIAS_SEMANA = [
  { numero: 1, nombre: "Lunes" },
  { numero: 2, nombre: "Martes" },
  { numero: 3, nombre: "Miércoles" },
  { numero: 4, nombre: "Jueves" },
  { numero: 5, nombre: "Viernes" },
  { numero: 6, nombre: "Sábado" },
  { numero: 7, nombre: "Domingo" },
];

function lugar(valor: unknown): LugarDia | null {
  return valor === "OFICINA" || valor === "HOME_OFFICE" ? valor : null;
}

/**
 * Arma el patrón editable desde lo que mandó el servidor: siempre los
 * siete días, de lunes a domingo.
 *
 * Un día que no viene (o viene sin mañana) arranca en oficina, igual que
 * el kiosco: es lo que se guardaba para él. Una tarde vacía significa
 * "igual que la mañana", la misma regla que aplica el servidor al guardar.
 */
export function patronDesdeServidor(dias: ModalidadDia[] | null | undefined): DiaPatron[] {
  const patron: DiaPatron[] = DIAS_SEMANA.map((dia) => ({
    diaSemana: dia.numero,
    nombre: dia.nombre,
    manana: "OFICINA",
    tarde: "OFICINA",
  }));
  for (const dia of dias ?? []) {
    const numero = Number(dia?.diaSemana);
    const manana = lugar(dia?.modalidadAm);
    if (!Number.isInteger(numero) || numero < 1 || numero > 7 || !manana) {
      continue;
    }
    const renglon = patron[numero - 1];
    renglon.manana = manana;
    renglon.tarde = lugar(dia.modalidadPm) ?? manana;
  }
  return patron;
}

/** Lo que viaja al servidor: los siete días, cada uno con su mañana y su tarde. */
export function aplanarPatron(patron: DiaPatron[]): ModalidadDia[] {
  return patron.map((dia) => {
    const renglon = new ModalidadDia();
    renglon.diaSemana = dia.diaSemana;
    renglon.modalidadAm = lugar(dia.manana) ?? "OFICINA";
    renglon.modalidadPm = lugar(dia.tarde) ?? renglon.modalidadAm;
    return renglon;
  });
}
